import axios, { AxiosResponse } from "axios";

import apiClient from "../../../../shared/infrastructure/api/client";
import type { Operation } from "../../domain/operations";
import type {
    JoinResponse,
    MapKind,
    PublicSystemicMap,
    ShareLink,
    SystemicMap,
    SystemicMapSummary,
} from "../../domain/types";

/**
 * Public (patient) client: NO Firebase auth interceptor.
 *
 * The patient has no account. The URL token is the whole authorisation, and
 * sending it through the authenticated client would attach a therapist's
 * credentials to a request that must never carry any.
 */
const publicClient = axios.create({
    baseURL: "/api",
    headers: { "Content-Type": "application/json" },
});

export interface CreateMapPayload {
    kind: MapKind;
    title: string;
    patientId?: string;
    couplePatientId?: string;
    groupPatientId?: string;
}

export const SystemicMapAPI = {
    // --- Therapist (authenticated) ---------------------------------------
    create: ({
        kind,
        title,
        patientId,
        couplePatientId,
        groupPatientId,
    }: CreateMapPayload): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.post("/systemic-maps", {
            kind,
            title,
            patient_id: patientId,
            couple_patient_id: couplePatientId,
            group_patient_id: groupPatientId,
        }),

    /**
     * One page of the therapist's maps, filtered by the server.
     *
     * Filtered there rather than here: a therapist with two hundred maps
     * should not be sent two hundred maps to narrow down, and a patient
     * filter must not be a suggestion.
     */
    list: (params?: {
        scope?: "individual" | "couple" | "group" | "open";
        kind?: MapKind;
        patient_id?: string;
        couple_patient_id?: string;
        group_patient_id?: string;
        since?: string;
        until?: string;
        offset?: number;
        limit?: number;
    }): Promise<AxiosResponse<{ items: SystemicMapSummary[]; total: number }>> =>
        apiClient.get("/systemic-maps", { params }),

    get: (mapId: string): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.get(`/systemic-maps/${mapId}`),

    rename: (mapId: string, title: string): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.patch(`/systemic-maps/${mapId}`, { title }),

    remove: (mapId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/systemic-maps/${mapId}`),

    applyOperations: (
        mapId: string,
        operations: Operation[],
    ): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.post(`/systemic-maps/${mapId}/ops`, { operations }),

    /** A short-lived permission to open the board's live channel. */
    wsTicket: (mapId: string): Promise<AxiosResponse<{ ticket: string }>> =>
        apiClient.post(`/systemic-maps/${mapId}/ws-ticket`),

    /**
     * Show the map in the patient's own portal, read-only, or stop showing it.
     *
     * Not the same thing as the share link: that is a live invitation to draw
     * together and it expires. This is the standing copy the family can come
     * back to between sessions, from their own account.
     */
    setPatientSharing: (
        mapId: string,
        shared: boolean,
    ): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.put(`/systemic-maps/${mapId}/patient-sharing`, { shared }),

    createShareLink: (mapId: string): Promise<AxiosResponse<ShareLink>> =>
        apiClient.post(`/systemic-maps/${mapId}/share-link`),

    revokeShareLink: (mapId: string): Promise<AxiosResponse<void>> =>
        apiClient.delete(`/systemic-maps/${mapId}/share-link`),

    uploadSnapshot: (mapId: string, blob: Blob): Promise<AxiosResponse<SystemicMap>> => {
        const form = new FormData();
        form.append("file", blob, "map.png");
        return apiClient.post(`/systemic-maps/${mapId}/snapshots`, form, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    // --- Patient (token-scoped, unauthenticated) -------------------------
    /**
     * The bytes of one filed image.
     *
     * Streamed through the API rather than fetched from a signed URL, the way
     * the constellation's snapshots are: it needs only read access, so it
     * works in every environment.
     */
    getSnapshot: (mapId: string, index: number): Promise<AxiosResponse<Blob>> =>
        apiClient.get(`/systemic-maps/${mapId}/snapshots/${index}`, {
            responseType: "blob",
        }),

    deleteSnapshot: (
        mapId: string,
        index: number,
    ): Promise<AxiosResponse<SystemicMap>> =>
        apiClient.delete(`/systemic-maps/${mapId}/snapshots/${index}`),

    join: (
        token: string,
        displayName: string,
        participantId?: string,
    ): Promise<AxiosResponse<JoinResponse>> =>
        publicClient.post(`/public/systemic-maps/${token}/join`, {
            display_name: displayName,
            participant_id: participantId,
        }),

    publicGet: (
        token: string,
        participantId: string,
    ): Promise<AxiosResponse<PublicSystemicMap>> =>
        publicClient.get(`/public/systemic-maps/${token}`, {
            params: { participant_id: participantId },
        }),

    publicWsTicket: (
        token: string,
        participantId: string,
    ): Promise<AxiosResponse<{ ticket: string }>> =>
        publicClient.post(`/public/systemic-maps/${token}/ws-ticket`, null, {
            params: { participant_id: participantId },
        }),

    publicApplyOperations: (
        token: string,
        participantId: string,
        operations: Operation[],
    ): Promise<AxiosResponse<PublicSystemicMap>> =>
        publicClient.post(`/public/systemic-maps/${token}/ops`, {
            participant_id: participantId,
            operations,
        }),
};
